import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  // Initialize with empty array, will be loaded from localStorage
  const [cartItems, setCartItems] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load cart from localStorage on mount
  useEffect(() => {
    console.log('🔄 CartContext: Loading cart from localStorage...');
    const savedCart = localStorage.getItem('cart');
    console.log('🔄 CartContext: Saved cart data:', savedCart);
    
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        console.log('🔄 CartContext: Parsed cart items:', parsedCart);
        setCartItems(parsedCart);
      } catch (error) {
        console.error('❌ CartContext: Error loading cart from localStorage:', error);
        setCartItems([]);
      }
    } else {
      console.log('🔄 CartContext: No saved cart found');
    }
    setIsLoaded(true);
  }, []);

  // Save cart to localStorage whenever cart changes (but only after initial load)
  useEffect(() => {
    if (isLoaded) {
      console.log('💾 CartContext: Saving cart to localStorage:', cartItems);
      localStorage.setItem('cart', JSON.stringify(cartItems));
    }
  }, [cartItems, isLoaded]);

  const addToCart = (product, quantity = 1) => {
    console.log('🛒 Adding to cart:', { product, quantity });
    console.log('🛒 Product ID:', product.id || product._id);
    
    setCartItems(prevItems => {
      // Use either id or _id for comparison
      const productId = product.id || product._id;
      const existingItem = prevItems.find(item => {
        const itemId = item.id || item._id;
        return itemId === productId;
      });
      
      console.log('🛒 Existing item found:', !!existingItem);
      console.log('🛒 Current cart items:', prevItems.length);
      
      if (existingItem) {
        return prevItems.map(item => {
          const itemId = item.id || item._id;
          return itemId === productId
            ? { ...item, quantity: item.quantity + quantity }
            : item;
        });
      } else {
        // Ensure the product has a consistent id field
        const productWithId = { ...product, id: productId };
        return [...prevItems, { ...productWithId, quantity }];
      }
    });
  };

  const removeFromCart = (productId) => {
    setCartItems(prevItems => prevItems.filter(item => {
      const itemId = item.id || item._id;
      return itemId !== productId;
    }));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    
    setCartItems(prevItems =>
      prevItems.map(item => {
        const itemId = item.id || item._id;
        return itemId === productId ? { ...item, quantity } : item;
      })
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartItemCount = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartSubtotal = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartDiscount = () => {
    // Calculate discount based on your business logic
    // For now, returning 0 - you can implement discount logic later
    return 0;
  };

  const getCartTax = () => {
    // Calculate tax based on your business logic
    // For now, returning 0 - you can implement tax logic later
    return 0;
  };

  const getCartShipping = () => {
    // Free shipping for orders over $100, otherwise $10
    const subtotal = getCartSubtotal();
    return subtotal >= 100 ? 0 : 10;
  };

  const getCartFinalTotal = () => {
    const subtotal = getCartSubtotal();
    const discount = getCartDiscount();
    const tax = getCartTax();
    const shipping = getCartShipping();
    return subtotal - discount + tax + shipping;
  };

  const value = {
    cartItems,
    isLoaded,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartItemCount,
    getCartTotal,
    getCartSubtotal,
    getCartDiscount,
    getCartTax,
    getCartShipping,
    getCartFinalTotal
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
